import { Injectable } from '@angular/core';
import {
  HttpInterceptor,
  HttpHandler,
  HttpRequest,
  HttpEvent,
  HttpErrorResponse
} from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { ErrorLoggerService } from './services/error-logger.service';
import { getAuth } from 'firebase/auth';

@Injectable()
export class ErrorLoggingInterceptor implements HttpInterceptor {
  constructor(private errorLogger: ErrorLoggerService) {}

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const startTime = Date.now();

    return next.handle(req).pipe(
      tap({
        next: (event) => {
          // Log successful requests that took too long (> 10 seconds)
          const duration = Date.now() - startTime;
          if (duration > 10000) {
            console.warn(`⚠️ Slow API Request: ${req.url} took ${duration}ms`);
          }
        }
      }),
      catchError((error: HttpErrorResponse) => {
        // Get current user ID if available
        const auth = getAuth();
        const userId = auth.currentUser?.uid || 'anonymous';

        // Determine API name from URL
        const apiName = this.extractApiName(req.url);

        // Skip noisy logging for 400 content-policy / filter (e.g. Stability "Filter reason: prompt")
        const body = error.error;
        const bodyStr = typeof body === 'string' ? body : (body ? JSON.stringify(body) : '');
        const isContentFilter = error.status === 400 &&
          (bodyStr?.toLowerCase().includes('filter') || (body && (body as any).reason?.includes?.('Filter')));

        if (!isContentFilter) {
          this.errorLogger.logApiError(
            error,
            apiName,
            {
              method: req.method,
              url: req.url,
              body: this.sanitizeRequestBody(req.body),
              headers: this.sanitizeHeaders(req.headers)
            },
            userId
          );
        }

        // Provide user-friendly error messages
        const userMessage = this.getUserFriendlyMessage(error, apiName);

        // Re-throw the error with enhanced information
        return throwError(() => ({
          ...error,
          userMessage,
          timestamp: new Date().toISOString(),
          apiName
        }));
      })
    );
  }

  private extractApiName(url: string): string {
    try {
      // Extract service name from URL
      if (url.includes('lambda-url')) {
        const match = url.match(/https:\/\/([a-z0-9]+)\.lambda-url/);
        return match ? `Lambda-${match[1].substring(0, 8)}` : 'Lambda';
      }

      if (url.includes('generativelanguage.googleapis.com')) {
        return 'Google Gemini';
      }

      if (url.includes('vertexai') || url.includes('aiplatform')) {
        return 'Google Vertex AI';
      }

      if (url.includes('openai.com')) {
        return 'OpenAI';
      }

      if (url.includes('x.ai')) {
        return 'Grok (X.AI)';
      }

      if (url.includes('cloudfunctions.net')) {
        const match = url.match(/cloudfunctions\.net\/([^/?]+)/);
        return match ? `Cloud Function: ${match[1]}` : 'Cloud Function';
      }

      if (url.includes('run.app')) {
        const match = url.match(/([^.]+)\.run\.app/);
        return match ? `Cloud Run: ${match[1]}` : 'Cloud Run';
      }

      if (url.includes('firestore')) {
        return 'Firestore';
      }

      if (url.includes('storage.googleapis.com')) {
        return 'Cloud Storage';
      }

      return 'Unknown API';
    } catch {
      return 'Unknown API';
    }
  }

  private getUserFriendlyMessage(error: HttpErrorResponse, apiName: string): string {
    // Check for HTML response (common with authentication issues)
    if (typeof error.error === 'string' &&
        (error.error.trim().startsWith('<!DOCTYPE') || error.error.trim().startsWith('<html'))) {
      return `${apiName} returned an authentication error. Please check your API credentials.`;
    }

    // Content policy / filter (e.g. Stability, OpenAI safety)
    const body = error.error;
    const bodyStr = typeof body === 'string' ? body : (body ? JSON.stringify(body) : '');
    if (error.status === 400 && (bodyStr?.toLowerCase().includes('filter') || (body && (body as any).reason?.includes?.('Filter')))) {
      return 'This prompt was blocked by the content policy. Please try a different description.';
    }

    // Status code specific messages
    switch (error.status) {
      case 0:
        return 'Unable to connect to the server. Please check your internet connection.';
      case 400:
        return 'Invalid request. Please check your input and try again.';
      case 401:
        return `Authentication failed for ${apiName}. Please sign in again.`;
      case 403:
        return `Access denied to ${apiName}. You may not have permission for this operation.`;
      case 404:
        return `${apiName} endpoint not found. The service may be unavailable.`;
      case 429:
        return `Rate limit exceeded for ${apiName}. Please wait a moment and try again.`;
      case 500:
        return `${apiName} server error. Please try again later.`;
      case 502:
      case 503:
      case 504:
        return `${apiName} is temporarily unavailable. Please try again in a few moments.`;
      default:
        return `An error occurred while contacting ${apiName}. Please try again.`;
    }
  }

  private sanitizeRequestBody(body: any): any {
    if (!body) return null;

    try {
      // Clone the body to avoid modifying the original
      const sanitized = JSON.parse(JSON.stringify(body));

      // Remove sensitive fields
      const sensitiveFields = ['password', 'token', 'apiKey', 'api_key', 'secret', 'authorization'];

      const removeSensitiveData = (obj: any): void => {
        if (typeof obj !== 'object' || obj === null) return;

        Object.keys(obj).forEach(key => {
          const lowerKey = key.toLowerCase();
          if (sensitiveFields.some(field => lowerKey.includes(field))) {
            obj[key] = '[REDACTED]';
          } else if (typeof obj[key] === 'object') {
            removeSensitiveData(obj[key]);
          }
        });
      };

      removeSensitiveData(sanitized);

      // Limit size
      const stringified = JSON.stringify(sanitized);
      return stringified.length > 1000
        ? JSON.parse(stringified.substring(0, 1000) + '..."}')
        : sanitized;
    } catch {
      return '[Could not sanitize body]';
    }
  }

  private sanitizeHeaders(headers: any): any {
    const sanitized: any = {};
    const sensitiveHeaders = ['authorization', 'x-api-key', 'x-auth-token'];

    try {
      headers.keys().forEach((key: string) => {
        const lowerKey = key.toLowerCase();
        if (sensitiveHeaders.includes(lowerKey)) {
          sanitized[key] = '[REDACTED]';
        } else {
          sanitized[key] = headers.get(key);
        }
      });
    } catch {
      return '[Could not sanitize headers]';
    }

    return sanitized;
  }
}
